/**
 * PDF Export V4 - ULTIMATE QUALITY EDITION
 * Uses Chart.js canvas rendering for perfect charts
 * Optimized spacing and layout
 */

import jsPDF from 'jspdf';
import Chart from 'chart.js/auto';
import bdLogo from "../../../../src/images/bd-white-logo.svg";
const COLORS = {
  // Professional color palette
  primary: [30, 64, 175],
  text: [31, 41, 55],
  textLight: [107, 114, 128],
  textMuted: [156, 163, 175],
  
  white: [255, 255, 255],
  bgLight: [249, 250, 251],
  bgAlt: [243, 244, 246],
  
  accent: [59, 130, 246],
  border: [229, 231, 235],
  
  // Professional chart colors
  chartColors: [
    'rgba(59, 130, 246, 0.8)',    // Blue
    'rgba(16, 185, 129, 0.8)',    // Green  
    'rgba(139, 92, 246, 0.8)',    // Purple
    'rgba(245, 158, 11, 0.8)',    // Amber
    'rgba(239, 68, 68, 0.8)',     // Red
    'rgba(236, 72, 153, 0.8)',    // Pink
    'rgba(20, 184, 166, 0.8)',    // Teal
    'rgba(249, 115, 22, 0.8)',    // Orange
    'rgba(99, 102, 241, 0.8)',    // Indigo
    'rgba(156, 163, 175, 0.8)',   // Gray
  ]
};

const FONT = {
  title: 20,
  h1: 14,
  h2: 12,
  h3: 11,
  body: 10,
  small: 9,
  tiny: 8
};

// ===== MAIN EXPORT FUNCTION =====
export async function exportConversationToPDF(conversationId, conversations) {
  try {
    const conversation = conversations[conversationId];
    if (!conversation?.messages?.length) {
      throw new Error('No messages to export');
    }

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const PAGE = {
      width: 210,
      height: 297,
      margin: 18,
      get contentWidth() { return this.width - (this.margin * 2); },
      footerY: 285
    };
    
    let y = PAGE.margin;
    
    // Header
    y = await addHeader(pdf, conversation, PAGE);
    
    // Messages
    for (const message of conversation.messages) {
      // Check page break
      if (y > PAGE.height - 60) {
        pdf.addPage();
        y = PAGE.margin;
      }
      
      if (message.type === 'USER' || message.sender === 'user') {
        y = addQuestion(pdf, message, PAGE, y);
      } else {
        y = await addAnswer(pdf, message, PAGE, y);
      }
    }
    
    // Page numbers
    addPageNumbers(pdf, PAGE);
    
    // Generate filename
    const filename = `${sanitizeFilename(conversation.title || 'report')}-${Date.now()}.pdf`;
    pdf.save(filename);
    
    return { success: true, filename };
  } catch (error) {
    console.error('PDF Export failed:', error);
    throw error;
  }
}
let cachedLogo = null;

async function getLogoBase64() {
  if (cachedLogo) return cachedLogo;
  
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 212;  // 2x for quality
      canvas.height = 52;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      cachedLogo = canvas.toDataURL('image/png');
      resolve(cachedLogo);
    };
    img.onerror = () => resolve(null);
    img.src = bdLogo;
  });
}
// ===== HEADER =====
async function addHeader(pdf, conversation, PAGE) {
  const headerHeight = 40;
  
  // Header bar
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, PAGE.width, headerHeight, 'F');
  
  // Logo - centered at top
  const logoBase64 = await getLogoBase64();
  const logoWidth = 42;
  const logoHeight = 10;
  
  if (logoBase64) {
    const logoX = (PAGE.width - logoWidth) / 2;
    pdf.addImage(logoBase64, 'PNG', logoX, 6, logoWidth, logoHeight);
  }
  
  // Title - centered below logo
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(...COLORS.white);
  const title = sanitizeText(conversation.title || 'Analysis Report').substring(0, 55);
  pdf.text(title, PAGE.width / 2, 24, { align: 'center' });
  
  // Date - centered below title
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(200, 210, 230);
  const date = new Date().toLocaleDateString('en-US', { 
    month: 'long', day: 'numeric', year: 'numeric' 
  });
  pdf.text(`Generated ${date}`, PAGE.width / 2, 32, { align: 'center' });
  
  return headerHeight + 8;
}


// ===== QUESTION BOX - FIXED SPACING =====
function addQuestion(pdf, message, PAGE, y) {
  const text = sanitizeText(message.content || message.text || '');
  if (!text) return y;
  
  pdf.setFontSize(FONT.h2);
  pdf.setFont('helvetica', 'bold');
  const lines = pdf.splitTextToSize(text, PAGE.contentWidth - 16);
  
  // MUCH TIGHTER - only 8mm padding total
  const boxHeight = lines.length * 5.5 + 8;
  
  // Background
  pdf.setFillColor(...COLORS.bgLight);
  pdf.roundedRect(PAGE.margin, y, PAGE.contentWidth, boxHeight, 2, 2, 'F');
  
  // Left accent
  pdf.setFillColor(...COLORS.accent);
  pdf.rect(PAGE.margin, y, 3, boxHeight, 'F');
  
  // Text
  pdf.setTextColor(...COLORS.text);
  pdf.text(lines, PAGE.margin + 10, y + 5.5);
  
  return y + boxHeight + 10;
}

// ===== ANSWER CONTENT =====
async function addAnswer(pdf, message, PAGE, y) {
  const content = message.content || message.text || '';
  
  // Parse markdown content
  const blocks = parseContent(content);
  
  for (const block of blocks) {
    if (y > PAGE.height - 50) {
      pdf.addPage();
      y = PAGE.margin;
    }
    
    switch (block.type) {
      case 'h1':
        y = addH1(pdf, block.text, PAGE, y);
        break;
      case 'h2':
        y = addH2(pdf, block.text, PAGE, y);
        break;
      case 'h3':
      case 'h4':
        y = addH3(pdf, block.text, PAGE, y);
        break;
      case 'paragraph':
        y = addParagraph(pdf, block.text, PAGE, y);
        break;
      case 'table':
        y = await addTable(pdf, block.headers, block.rows, PAGE, y);
        break;
      case 'list':
        y = addList(pdf, block.items, PAGE, y);
        break;
      case 'divider':
        y += 6;
        break;
    }
  }
  
  // CHARTS - HIGH QUALITY RENDERING
  if (message.charts?.length) {
    for (const chart of message.charts) {
      if (y > PAGE.height - 120) {
        pdf.addPage();
        y = PAGE.margin;
      }
      y = await addChartHighQuality(pdf, chart, PAGE, y);
    }
  }
  
  return y + 8;
}

// ===== HIGH QUALITY CHART RENDERING =====
async function addChartHighQuality(pdf, chartData, PAGE, y) {
  const data = extractChartData(chartData);
  
  if (!data) {
    return addChartPlaceholder(pdf, chartData.title, PAGE, y);
  }

  const title = sanitizeText(chartData.title || 'Chart');
  
  // Calculate title height FIRST
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(FONT.h2);
  const titleLines = pdf.splitTextToSize(title, PAGE.contentWidth - 16);
  const titleHeight = titleLines.length * 5.5 + 10; // line height + padding
  
  const chartHeight = 120;
  const totalBoxHeight = chartHeight + titleHeight;
  
  // Check if we need a new page
  if (y + totalBoxHeight > PAGE.height - 30) {
    pdf.addPage();
    y = PAGE.margin;
  }
  
  // Draw container box
  pdf.setFillColor(...COLORS.white);
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(PAGE.margin, y, PAGE.contentWidth, totalBoxHeight, 3, 3, 'FD');
  
  // Draw wrapped title
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(FONT.h2);
  pdf.setTextColor(...COLORS.text);
  
  let titleY = y + 8;
  for (const line of titleLines) {
    pdf.text(line, PAGE.margin + 8, titleY);
    titleY += 5.5;
  }
  
  // Rest of chart rendering...
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '3200px';
  container.style.height = '1600px';
  document.body.appendChild(container);
  
  const canvas = document.createElement('canvas');
  canvas.width = 3200;
  canvas.height = 1600;
  container.appendChild(canvas);
  
  const ctx = canvas.getContext('2d');
  const chartType = detectChartType(chartData, data);
  const chartConfig = buildChartConfig(chartType, data, chartData);

  try {
    const chart = new Chart(ctx, chartConfig);
    
    await new Promise((resolve) => {
      setTimeout(() => {
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        if (imgData && imgData.startsWith('data:image/png')) {
          pdf.addImage(
            imgData, 
            'PNG', 
            PAGE.margin + 8, 
            y + titleHeight,  // Start chart AFTER title
            PAGE.contentWidth - 16,
            chartHeight - 10
          );
        }
        
        chart.destroy();
        document.body.removeChild(container);
        resolve();
      }, 800);
    });
    
  } catch (error) {
    console.error('Chart error:', error);
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
    return addChartPlaceholder(pdf, chartData.title, PAGE, y);
  }
  
  return y + totalBoxHeight + 12;
}


function buildChartConfig(type, data, chartData = null) {
  // Extract server-provided options if available
  const serverOptions = chartData?.config?.options || {};
  const isHorizontal = serverOptions.indexAxis === 'y' ||
                       chartData?.metadata?.orientation === 'horizontal';

  const baseConfig = {
    type: type,
    data: {
      labels: data.labels || [],
      datasets: (data.datasets || []).map((dataset, idx) => {
        const defaultColor = COLORS.chartColors[idx % COLORS.chartColors.length];
        return {
          ...dataset,
          // Preserve server colors if they exist, otherwise use defaults
          backgroundColor: dataset.backgroundColor || defaultColor,
          borderColor: dataset.borderColor || defaultColor.replace('0.8', '1'),
          borderWidth: dataset.borderWidth || 2,
          borderRadius: dataset.borderRadius || 4,
          // Preserve line chart properties
          ...(type === 'line' && {
            fill: dataset.fill ?? false,
            tension: dataset.tension ?? 0.3,
            pointRadius: 6,
            pointHoverRadius: 8,
          }),
        };
      })
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      // Apply horizontal orientation if specified
      ...(isHorizontal && { indexAxis: 'y' }),
      plugins: {
        legend: {
          display: type === 'pie' || type === 'doughnut' || (data.datasets?.length > 1),
          position: type === 'pie' || type === 'doughnut' ? 'right' : 'top',
          labels: {
            font: {
              size: 48,
              family: 'Helvetica',
              weight: 'bold'
            },
            padding: 25,
            boxWidth: 40
          }
        },
        tooltip: {
          enabled: false
        },
        title: {
          display: false
        }
      }
    }
  };
  
  if (type === 'bar' || type === 'line') {
    // Pull axis titles from server options if available
    const serverXTitle = serverOptions.scales?.x?.title?.text;
    const serverYTitle = serverOptions.scales?.y?.title?.text;

    baseConfig.options.scales = {
      x: {
        grid: {
          display: type === 'line',
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 3
        },
        ticks: {
          font: { size: 40, weight: 'bold' },
          maxRotation: isHorizontal ? 0 : 45,
          minRotation: 0
        },
        ...(serverXTitle && {
          title: { display: true, text: serverXTitle, font: { size: 36, weight: 'bold' } }
        })
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 3
        },
        ticks: {
          font: { size: 40, weight: 'bold' }
        },
        ...(serverYTitle && {
          title: { display: true, text: serverYTitle, font: { size: 36, weight: 'bold' } }
        })
      }
    };
  }
  
  if (type === 'pie' || type === 'doughnut') {
    baseConfig.options.plugins.legend.position = 'right';
    baseConfig.options.plugins.legend.labels.padding = 30;
    baseConfig.options.plugins.legend.labels.font.size = 56;
  }
  
  return baseConfig;
}



// ===== CHART TYPE DETECTION =====
function detectChartType(chart, data) {
  // NEW: Check structured config first (new server format)
  if (chart.config?.type) {
    return chart.config.type;
  }

  // NEW: Check metadata (also available in new format)
  if (chart.metadata?.chartType) {
    return chart.metadata.chartType;
  }

  // LEGACY: Check HTML for explicit type
  const htmlLower = (chart.html || '').toLowerCase();
  
  if (htmlLower.includes("type: 'pie'") || htmlLower.includes('type: "pie"')) return 'pie';
  if (htmlLower.includes("type: 'doughnut'") || htmlLower.includes('type: "doughnut"')) return 'doughnut';
  if (htmlLower.includes("type: 'line'") || htmlLower.includes('type: "line"')) return 'line';
  if (htmlLower.includes("type: 'bar'") || htmlLower.includes('type: "bar"')) return 'bar';
  
  // Heuristic: if single dataset with multiple colors, likely pie
  if (data.datasets?.length === 1 && data.datasets[0].backgroundColor?.length > 1) {
    return 'pie';
  }
  
  // Default to bar
  return 'bar';
}

// ===== CHART DATA EXTRACTION =====
function extractChartData(chart) {
  // NEW: Check for structured config object first (new server format)
  if (chart.config?.data) {
    return chart.config.data;
  }

  // LEGACY: Fall back to parsing from HTML if config not available
  if (!chart.html) return null;
  
  try {
    // Method 1: Parse from JSON.parse() call
    const jsonMatch = chart.html.match(/data:\s*JSON\.parse\(['"](.*?)['"]\)/s);
    if (jsonMatch) {
      const jsonStr = jsonMatch[1]
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
      return JSON.parse(jsonStr);
    }
    
    // Method 2: Parse from direct object
    const dataMatch = chart.html.match(/data:\s*(\{[\s\S]*?\}),?\s*options:/);
    if (dataMatch) {
      const dataStr = dataMatch[1]
        .replace(/'/g, '"')
        .replace(/(\w+):/g, '"$1":');
      return JSON.parse(dataStr);
    }
    
  } catch (e) {
    console.error('Chart data extraction failed:', e);
  }
  
  return null;
}

// ===== TYPOGRAPHY =====
function addH1(pdf, text, PAGE, y) {
  const clean = sanitizeText(text);
  if (!clean) return y;
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(FONT.h1);
  pdf.setTextColor(...COLORS.primary);
  
  const lines = pdf.splitTextToSize(clean, PAGE.contentWidth);
  for (const line of lines) {
    if (y > PAGE.height - 20) {
      pdf.addPage();
      y = PAGE.margin;
    }
    pdf.text(line, PAGE.margin, y);
    y += 6.5;
  }
  
  return y + 3;
}

function addH2(pdf, text, PAGE, y) {
  const clean = sanitizeText(text);
  if (!clean) return y;
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(FONT.h2);
  pdf.setTextColor(...COLORS.text);
  
  // WRAP LONG TITLES
  const lines = pdf.splitTextToSize(clean, PAGE.contentWidth);
  for (const line of lines) {
    if (y > PAGE.height - 20) {
      pdf.addPage();
      y = PAGE.margin;
    }
    pdf.text(line, PAGE.margin, y);
    y += 5.5;  // Line height for h2
  }
  
  return y + 2;  // Small gap after heading
}

function addH3(pdf, text, PAGE, y) {
  const clean = sanitizeText(text);
  if (!clean) return y;
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(FONT.h3);
  pdf.setTextColor(...COLORS.textLight);
  
  const lines = pdf.splitTextToSize(clean, PAGE.contentWidth);
  for (const line of lines) {
    if (y > PAGE.height - 20) {
      pdf.addPage();
      y = PAGE.margin;
    }
    pdf.text(line, PAGE.margin, y);
    y += 5;
  }
  
  return y + 2;
}

function addParagraph(pdf, text, PAGE, y) {
  const clean = sanitizeText(text);
  if (!clean) return y;
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(FONT.body);
  pdf.setTextColor(...COLORS.text);
  
  const lines = pdf.splitTextToSize(clean, PAGE.contentWidth);
  for (const line of lines) {
    if (y > PAGE.height - 20) {
      pdf.addPage();
      y = PAGE.margin;
    }
    pdf.text(line, PAGE.margin, y);
    y += 4.5;
  }
  
  return y + 3;
}

function addList(pdf, items, PAGE, y) {
  pdf.setFontSize(FONT.body);
  
  for (const item of items) {
    if (y > PAGE.height - 20) {
      pdf.addPage();
      y = PAGE.margin;
    }
    
    const clean = sanitizeText(item.text);
    if (!clean) continue;
    
    // Bullet
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.textLight);
    const prefix = item.number ? `${item.number}.` : '•';
    pdf.text(prefix, PAGE.margin + 2, y);
    
    // Text
    pdf.setTextColor(...COLORS.text);
    const lines = pdf.splitTextToSize(clean, PAGE.contentWidth - 12);
    pdf.text(lines[0], PAGE.margin + 10, y);
    y += 4.5;
    
    for (let i = 1; i < lines.length; i++) {
      pdf.text(lines[i], PAGE.margin + 10, y);
      y += 4.5;
    }
    y += 1;
  }
  
  return y + 3;
}

// ===== TABLES =====

// Helper function to load an image and return as base64 data URL
async function loadImageAsBase64(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        // Use smaller size for table images
        const maxSize = 100;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataUrl);
      } catch (e) {
        console.warn('Failed to convert image to base64:', e);
        resolve(null);
      }
    };
    
    img.onerror = () => {
      console.warn('Failed to load image:', url);
      resolve(null);
    };
    
    // Add timeout to prevent hanging
    setTimeout(() => resolve(null), 5000);
    
    img.src = url;
  });
}

async function addTable(pdf, headers, rows, PAGE, y) {
  if (!headers?.length && !rows?.length) return y;
  
  const numCols = headers?.length || rows[0]?.length || 0;
  if (numCols === 0) return y;
  
  // Check if any cells have images
  const hasImages = rows.some(row => 
    row.some(cell => cell?.imageUrl)
  );
  
  const colWidth = PAGE.contentWidth / numCols;
  const imageSize = 8; // Image size in mm
  const minRowHeight = hasImages ? Math.max(10, imageSize + 2) : 8;
  const lineHeight = 4; // Height per line of text
  const cellPadding = 2;
  
  // Pre-load all images if present
  const imageCache = new Map();
  if (hasImages) {
    const imageUrls = new Set();
    rows.forEach(row => {
      row.forEach(cell => {
        if (cell?.imageUrl) {
          imageUrls.add(cell.imageUrl);
        }
      });
    });
    
    // Load images in parallel with a timeout
    const loadPromises = Array.from(imageUrls).map(async url => {
      const base64 = await loadImageAsBase64(url);
      if (base64) {
        imageCache.set(url, base64);
      }
    });
    
    await Promise.all(loadPromises);
  }
  
  // Helper function to calculate row height based on content
  const calculateRowHeight = (row) => {
    pdf.setFontSize(FONT.small);
    let maxLines = 1;
    
    row.forEach((cell, colIdx) => {
      if (colIdx >= numCols) return;
      
      const cellText = typeof cell === 'object' ? cell.text : String(cell || '');
      const cellImageUrl = typeof cell === 'object' ? cell.imageUrl : null;
      
      if (cellText) {
        const textWidth = cellImageUrl && imageCache.has(cellImageUrl)
          ? colWidth - imageSize - 6
          : colWidth - 4;
        const lines = pdf.splitTextToSize(sanitizeText(cellText), textWidth);
        maxLines = Math.max(maxLines, lines.length);
      }
    });
    
    return Math.max(minRowHeight, maxLines * lineHeight + cellPadding * 2);
  };
  
  let startY = y;
  
  // Header
  if (headers?.length) {
    const headerHeight = minRowHeight;
    
    // Check if header fits on page
    if (y + headerHeight > PAGE.height - 30) {
      pdf.addPage();
      y = PAGE.margin;
      startY = y;
    }
    
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(PAGE.margin, y, PAGE.contentWidth, headerHeight, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(FONT.small);
    pdf.setTextColor(...COLORS.white);
    
    headers.forEach((header, i) => {
      const cellX = PAGE.margin + (i * colWidth) + cellPadding;
      const textWidth = colWidth - (cellPadding * 2);
      const lines = pdf.splitTextToSize(sanitizeText(String(header)), textWidth);
      
      // Render wrapped header text
      lines.forEach((line, lineIdx) => {
        pdf.text(line, cellX, y + cellPadding + 3 + (lineIdx * lineHeight));
      });
    });
    y += headerHeight;
  }
  
  // Rows
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(FONT.small);
  
  for (const [rowIdx, row] of rows.entries()) {
    const rowHeight = calculateRowHeight(row);
    
    // Check if we need a new page
    if (y + rowHeight > PAGE.height - 30) {
      // Draw border for current section before page break
      pdf.setDrawColor(...COLORS.border);
      pdf.setLineWidth(0.3);
      pdf.rect(PAGE.margin, startY, PAGE.contentWidth, y - startY);
      
      pdf.addPage();
      y = PAGE.margin;
      startY = y;
      
      // Redraw header on new page
      if (headers?.length) {
        const headerHeight = minRowHeight;
        pdf.setFillColor(...COLORS.primary);
        pdf.rect(PAGE.margin, y, PAGE.contentWidth, headerHeight, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT.small);
        pdf.setTextColor(...COLORS.white);
        
        headers.forEach((header, i) => {
          const cellX = PAGE.margin + (i * colWidth) + cellPadding;
          const textWidth = colWidth - (cellPadding * 2);
          const lines = pdf.splitTextToSize(sanitizeText(String(header)), textWidth);
          
          lines.forEach((line, lineIdx) => {
            pdf.text(line, cellX, y + cellPadding + 3 + (lineIdx * lineHeight));
          });
        });
        y += headerHeight;
      }
    }
    
    const bgColor = rowIdx % 2 === 0 ? COLORS.white : COLORS.bgLight;
    pdf.setFillColor(...bgColor);
    pdf.rect(PAGE.margin, y, PAGE.contentWidth, rowHeight, 'F');
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    
    row.forEach((cell, colIdx) => {
      if (colIdx >= numCols) return;
      const cellX = PAGE.margin + (colIdx * colWidth) + cellPadding;
      
      // Handle cell object with text and imageUrl
      const cellText = typeof cell === 'object' ? cell.text : String(cell || '');
      const cellImageUrl = typeof cell === 'object' ? cell.imageUrl : null;
      
      // Try to render image if available
      let imageWidth = 0;
      if (cellImageUrl && imageCache.has(cellImageUrl)) {
        try {
          const imgData = imageCache.get(cellImageUrl);
          const imgX = cellX;
          const imgY = y + cellPadding;
          pdf.addImage(imgData, 'JPEG', imgX, imgY, imageSize, imageSize);
          imageWidth = imageSize + 2;
        } catch (e) {
          console.warn('Failed to add image to PDF:', e);
        }
      }
      
      // Render wrapped text
      if (cellText) {
        const textX = cellX + imageWidth;
        const textWidth = colWidth - (cellPadding * 2) - imageWidth;
        const lines = pdf.splitTextToSize(sanitizeText(cellText), textWidth);
        
        lines.forEach((line, lineIdx) => {
          pdf.text(line, textX, y + cellPadding + 3 + (lineIdx * lineHeight));
        });
      }
    });
    y += rowHeight;
  }
  
  // Border
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.3);
  pdf.rect(PAGE.margin, startY, PAGE.contentWidth, y - startY);
  
  return y + 10;
}

// ===== CHART PLACEHOLDER =====
function addChartPlaceholder(pdf, title, PAGE, y) {
  pdf.setFillColor(...COLORS.bgLight);
  pdf.roundedRect(PAGE.margin, y, PAGE.contentWidth, 30, 2, 2, 'F');
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(FONT.small);
  pdf.setTextColor(...COLORS.textMuted);
  pdf.text(`Chart: ${sanitizeText(title || 'Untitled')}`, PAGE.margin + 6, y + 12);
  pdf.text('Data could not be rendered', PAGE.margin + 6, y + 20);
  
  return y + 38;
}

// ===== PAGE NUMBERS =====
function addPageNumbers(pdf, PAGE) {
  const total = pdf.getNumberOfPages();
  
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    
    // Footer line
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.2);
    pdf.line(PAGE.margin, PAGE.footerY, PAGE.width - PAGE.margin, PAGE.footerY);
    
    // Page number
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(FONT.tiny);
    pdf.setTextColor(...COLORS.textMuted);
    pdf.text(`Page ${i} of ${total}`, PAGE.width / 2, PAGE.footerY + 6, { align: 'center' });
  }
}

// ===== CONTENT PARSING =====
function parseContent(content) {
   content = content
    .replace(/([.!?:;])\s*(#{1,4}\s)/g, '$1\n\n$2')  // After punctuation
    .replace(/([a-z0-9])(#{1,4}\s)/gi, '$1\n\n$2');   // 
  const blocks = [];
  const lines = content.split('\n');
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    
    // Divider
    if (/^[-*_]{3,}$/.test(line)) {
      blocks.push({ type: 'divider' });
      i++; continue;
    }
    
    // Heading
    const hMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (hMatch) {
      blocks.push({ type: `h${hMatch[1].length}`, text: cleanMd(hMatch[2]) });
      i++; continue;
    }
    
    // Table
    if (line.startsWith('|')) {
      const { headers, rows, end } = parseTable(lines, i);
      if (rows.length || headers.length) {
        blocks.push({ type: 'table', headers, rows });
      }
      i = end; continue;
    }
    
    // List
    if (/^(\d+\.|-|\*)\s/.test(line)) {
      const { items, end } = parseList(lines, i);
      blocks.push({ type: 'list', items });
      i = end; continue;
    }
    
    // Paragraph
    let para = line;
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}(\s|$)|[-*_]{3,}$|\||\d+\.|-\s|\*\s)/.test(lines[i].trim())) {
      // FIX PART 2: Check if line contains inline heading marker
      if (lines[i].includes('##')) {
        break;
      }
      para += ' ' + lines[i].trim();
      i++;
    }
    blocks.push({ type: 'paragraph', text: cleanMd(para) });
  }
  
  return blocks;
}

function cleanMd(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    // Remove <imageurl> tags but keep them for separate processing
    .replace(/<imageurl>\s*https?:\/\/[^\s<]+\s*<\/imageurl>/gi, '')
    // Remove markdown images but keep them for separate processing
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .trim();
}

// Extract image URL from cell content (supports both <imageurl> tags and markdown images)
function extractImageFromCell(cellText) {
  if (!cellText) return null;
  
  // Check for <imageurl> tag
  const imageurlMatch = cellText.match(/<imageurl>\s*(https?:\/\/[^\s<]+)\s*<\/imageurl>/i);
  if (imageurlMatch) {
    return imageurlMatch[1].trim();
  }
  
  // Check for markdown image
  const mdImageMatch = cellText.match(/!\[.*?\]\((https?:\/\/[^\s)]+)\)/);
  if (mdImageMatch) {
    return mdImageMatch[1].trim();
  }
  
  return null;
}

function parseTable(lines, start) {
  const headers = [];
  const rows = [];
  let i = start;
  
  if (lines[i]?.includes('|')) {
    const cells = lines[i].split('|').map(c => c.trim()).filter(c => c && !/^-+$/.test(c));
    if (cells.length) headers.push(...cells.map(cleanMd));
    i++;
  }
  
  if (lines[i]?.match(/^\|?\s*[-:]+/)) i++;
  
  while (i < lines.length && lines[i]?.includes('|')) {
    const line = lines[i].trim();
    if (!line || /^[-:|s]+$/.test(line)) { i++; continue; }
    const rawCells = line.split('|').map(c => c.trim()).filter(c => c);
    
    // Process each cell, extracting text and image separately
    const processedCells = rawCells.map(cell => {
      const imageUrl = extractImageFromCell(cell);
      const text = cleanMd(cell);
      return { text, imageUrl };
    });
    
    if (processedCells.length) rows.push(processedCells);
    i++;
  }
  
  return { headers, rows, end: i };
}

function parseList(lines, start) {
  const items = [];
  let i = start;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }
    
    const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    
    if (numMatch) {
      items.push({ number: numMatch[1], text: cleanMd(numMatch[2]) });
      i++;
    } else if (bulletMatch) {
      items.push({ bullet: true, text: cleanMd(bulletMatch[1]) });
      i++;
    } else break;
  }
  
  return { items, end: i };
}

// ===== UTILITIES =====

function sanitizeText(text) {
  if (!text) return '';
  
  // First, normalize and preserve currency symbols
  let cleaned = text
    // Preserve common currency symbols by temporarily replacing them
    .replace(/₹/g, 'INR_TEMP')
    .replace(/¥/g, 'YEN_TEMP')
    .replace(/€/g, 'EUR_TEMP')
    .replace(/£/g, 'GBP_TEMP')
    .replace(/\$/g, 'USD_TEMP')
    .replace(/₩/g, 'KRW_TEMP')
    .replace(/฿/g, 'THB_TEMP')
    .replace(/₱/g, 'PHP_TEMP')
    
    // Remove arrows
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/↔/g, '<->')
    .replace(/⟶/g, '-->')
    
    // Remove zero-width and special spacing characters
    .replace(/[\u200B-\u200D\uFEFF]/g, '')      // Zero-width chars
    .replace(/[\u00A0]/g, ' ')                   // Non-breaking space -> regular space
    .replace(/[\u2000-\u200A]/g, ' ')            // Various Unicode spaces
    .replace(/[\u202F\u205F\u3000]/g, ' ')       // More special spaces
    
    // Remove emojis
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
    .replace(/[\u{2600}-\u{27BF}]/gu, '')
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
    
    // Normalize multiple spaces to single
    .replace(/\s+/g, ' ')
    .trim()
    
    // Restore currency symbols with ASCII-safe alternatives
    .replace(/INR_TEMP/g, 'Rs.')       // Indian Rupee as Rs.
    .replace(/YEN_TEMP/g, 'Yen ')      // Yen
    .replace(/EUR_TEMP/g, 'EUR ')      // Euro
    .replace(/GBP_TEMP/g, 'GBP ')      // British Pound
    .replace(/USD_TEMP/g, '$')         // US Dollar (safe)
    .replace(/KRW_TEMP/g, 'KRW ')      // Korean Won
    .replace(/THB_TEMP/g, 'THB ')      // Thai Baht
    .replace(/PHP_TEMP/g, 'PHP ');     // Philippine Peso
  
  return cleaned;
}

function sanitizeFilename(text) {
  return text
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-+/g, '-')
    .substring(0, 40)
    .replace(/^-|-$/g, '');
}

// ===== EXPORT WITH FEEDBACK =====
export async function exportConversationWithFeedback(conversationId, conversations, callbacks = {}) {
  try {
    callbacks.onStart?.();
    const result = await exportConversationToPDF(conversationId, conversations);
    callbacks.onSuccess?.(result);
    return result;
  } catch (error) {
    callbacks.onError?.(error);
    throw error;
  }
}
